import { JSX, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import UpgradeIcon from "@mui/icons-material/Upgrade";

import ActionButton from "lib/components/button/ActionButton";

import { ResponseState } from "utils/fetch/FetchResponse";
import useFetch from "utils/fetch/useFetch";
import useGet from "utils/fetch/useGet";
import { isTrajectXUser } from "utils/UserUtils";
import { asTitle } from "utils/Utils";

import { usePrincipal } from "authentication/AuthProvider";
import BlueprintClient from "core/blueprint/base/BlueprintClient";
import LibraryClient from "core/library/LibraryClient";

export type BlueprintLibraryInfoProps = {
	id: number;
	enabled: boolean;
	blueprintId: number;
	blueprintVersion: number;

	onUpdate: () => void;
};

export default function BlueprintLibraryInfo(
	props: Readonly<BlueprintLibraryInfoProps>
): JSX.Element {
	const { t } = useTranslation();

	const principal = usePrincipal();

	const { response } = useGet(LibraryClient.get(props.blueprintId));
	const { call } = useFetch();

	const canUpdate = useMemo(() => {
		if (response.state !== ResponseState.SUCCESS) {
			return false;
		}

		return props.enabled && props.blueprintVersion < response.data.version;
	}, [props.enabled, props.blueprintVersion, response]);

	const handleUpdate = useCallback(
		(): Promise<void> =>
			call(BlueprintClient.updateFromLibrary(props.id)).then(
				props.onUpdate
			),
		[props.id, props.onUpdate, call]
	);

	if (!isTrajectXUser(principal?.userEmail ?? "")) {
		return <></>;
	}

	if (response.state === ResponseState.LOADING) {
		return <></>;
	}

	if (response.state === ResponseState.ERROR) {
		return (
			<Typography variant="body1">
				{t("loadDocumentError", { document: "libraryBlueprint" })}
			</Typography>
		);
	}

	return (
		<Grid container>
			<Grid item container alignItems="center">
				<Grid item xs={8} sm={5} md={4} lg={3}>
					<Typography variant="subtitle1">
						{asTitle(t("blueprint"))}
					</Typography>
				</Grid>
				<Grid item xs={4} sm={7} md={8} lg={9}>
					<Typography variant="body1">
						{response.data.name}
					</Typography>
				</Grid>
			</Grid>
			<Grid item container alignItems="center">
				<Grid item xs={8} sm={5} md={4} lg={3}>
					<Typography variant="subtitle1">
						{asTitle(t("version"))}
					</Typography>
				</Grid>
				<Grid
					item
					container
					xs={4}
					sm={7}
					md={8}
					lg={9}
					alignItems="center"
					columnSpacing={1}>
					<Grid item>
						<Typography variant="body1">
							{props.blueprintVersion}/{response.data.version}
						</Typography>
					</Grid>
					<Grid item>
						<ActionButton
							icon={<UpgradeIcon />}
							size="small"
							tooltip={asTitle(t("update"))}
							disabled={!canUpdate}
							dialogTitle={t("updateBlueprintVersionTitle")}
							dialogText={t("updateBlueprintVersionMessage")}
							dialogCancelLabel={t("cancel")}
							dialogConfirmLabel={t("update")}
							onAction={handleUpdate}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
